import Color from '@/Enums/Color.enum';
import { ModalType } from '@/Enums/Whitelabels/ModalType.enum';
import WhitelabelDomainStatus from '@/Enums/Whitelabels/WhitelabelDomainStatus.enum';
import { WhitelabelType } from '@/Enums/Whitelabels/WhitelabelType.enum';
import { ApprovalStatus } from '@/Interfaces/ApprovalStatus.enum';
import { IWhitelabelFormData } from '@/Interfaces/Whitelabels/WhitelabelFormData.interface';
import ModalHeaderIcon from '@/components/Icons/ModalHeaderIcon';
import LoadingBackdropWrapper from '@/components/Modal/common/LoadingBackdropWrapper';
import ModalTitleCard from '@/components/Modal/common/ModalTitleCard';
import { MODAL_TITLES, WHITELABEL_TYPE_LABELS } from '@/constants/whitelabels';
import { getComputedColorValue } from '@/helpers/Whitelabels/colorPicker';
import { getDefaultNewWhitelabel, getDirtyValues, validateImage } from '@/helpers/Whitelabels/whitelabelForm';
import { handleApiValidationErrors } from '@/helpers/form';
import { isSuperAdminRole } from '@/helpers/roles';
import { rerunVerification } from '@/store/api/whitelabels.api';
import { useAppSelector } from '@/store/hooks';
import {
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { isAxiosError } from 'axios';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, FormProvider, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import ColorInput from './ColorInput';
import DomainStatus from './DomainStatus';
import ImageUpload from './ImageUpload';
import SwitchToggle from './SwitchToggle';

const getDomainValidationRules = (fieldName: string) => ({
  pattern: {
    value: /^(https?:\/\/)?([\da-z.-]+)\.([a-z]{2,6})([/\w .-]*)*\/?$/i,
    message: 'Please enter a valid domain',
  },
  validate: (value: string): string | boolean => {
    if (!value || value.trim() === '') {
      return true;
    }
    if (/\?|(\/.+$)/.test(value?.replace(/^(https?:\/\/)?/, '') || '')) {
      return `${fieldName} should not have a path or query. For example www.ford.com/ is valid; www.ford.com/something and www.ford.com?param=something are not.`;
    }
    if (!/^(https?:\/\/)?www./.test(value ?? '') && (value ?? '').split('.').length <= 2) {
      return `Second level ${fieldName} must start with www.`;
    }
    return true;
  },
});

interface IProps {
  open: boolean;
  onClose: () => void;
  modalType: ModalType;
  onSubmit: (whitelabel: Partial<IWhitelabelFormData>) => Promise<void>;
  loading?: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setSelectedWhitelabel: (whitelabel: IWhitelabelFormData) => void;
  fetchWhitelabels: () => void;
}

const WhitelabelModal = ({
  open,
  onClose,
  modalType,
  onSubmit,
  loading,
  setLoading,
  setSelectedWhitelabel,
  fetchWhitelabels,
}: IProps) => {
  const { selectedWhitelabel, orgs } = useAppSelector(state => state.whitelabels);
  const { role: meRole, whitelabel: meWhitelabel } = useAppSelector(state => state.me);

  //#region form
  const formMethods = useForm({
    reValidateMode: 'onBlur',
    defaultValues: useMemo(() => {
      if (modalType === ModalType.View) {
        return selectedWhitelabel;
      }
      return getDefaultNewWhitelabel(meRole!, meWhitelabel!);
    }, [meRole, meWhitelabel, modalType, selectedWhitelabel]),
  });

  const {
    reset,
    handleSubmit,
    watch,
    getValues,
    control,
    formState: { dirtyFields, isDirty },
    setError,
  } = formMethods;

  const [sidebarBg, setSidebarBg] = useState<null | string>(null);
  const [loginBg, setLoginBg] = useState<null | string>(null);

  useEffect(() => {
    setSidebarBg(getValues('sidebarBg') ?? null);
    setLoginBg(getValues('loginBg') ?? null);
  }, [getValues, reset]);

  useEffect(() => {
    if (modalType === ModalType.View) {
      reset(selectedWhitelabel);
    }
  }, [modalType, reset, selectedWhitelabel]);

  const whitelabelType = useWatch({ control, name: 'type' });
  const organization = useWatch({ control, name: 'organization' });

  const sidebarBgRef = useRef<HTMLDivElement>(null);
  const loginBgRef = useRef<HTMLDivElement>(null);
  const buttonBgRef = useRef<HTMLDivElement>(null);
  const sidebarTextColorRef = useRef<HTMLDivElement>(null);

  const disabled = useMemo(() => modalType === ModalType.View, [modalType]);
  const controlLabel = useMemo(() => WHITELABEL_TYPE_LABELS[whitelabelType], [whitelabelType]);
  const showAdminLevel = useMemo(() => isSuperAdminRole(meRole), [meRole]);
  const domainDisabled = useMemo(
    () =>
      [WhitelabelDomainStatus.AwaitingDnsResponse, WhitelabelDomainStatus.VerifyingCustomDomain].includes(
        selectedWhitelabel?.domainStatus,
      ),
    [selectedWhitelabel?.domainStatus],
  );
  const externalDomainDisabled = useMemo(
    () =>
      [WhitelabelDomainStatus.AwaitingDnsResponse, WhitelabelDomainStatus.VerifyingCustomDomain].includes(
        selectedWhitelabel?.domainExternalStatus,
      ),
    [selectedWhitelabel?.domainExternalStatus],
  );

  useEffect(() => {
    const subscription = watch((_value, { name }) => {
      if (name === 'loginBg') {
        setTimeout(() => setLoginBg(getComputedColorValue(loginBgRef)), 0);
        return;
      }
      if (name === 'sidebarBg') {
        setTimeout(() => setSidebarBg(getComputedColorValue(sidebarBgRef)), 0);
        return;
      }
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [watch]);
  //#endregion

  const handleClose = useCallback(() => {
    onClose();
    reset();
    setSidebarBg(null);
    setLoginBg(null);
  }, [onClose, reset]);

  const getColorSubmitValues = useCallback(
    () => ({
      loginBg: getComputedColorValue(loginBgRef) ?? '',
      sidebarBg: getComputedColorValue(sidebarBgRef) ?? '',
      buttonBg: getComputedColorValue(buttonBgRef) ?? '',
      sidebarTextColor: getComputedColorValue(sidebarTextColorRef) ?? '',
    }),
    [],
  );

  const onFormSubmit: SubmitHandler<IWhitelabelFormData> = useCallback(
    async data => {
      let formData: Partial<IWhitelabelFormData>;
      if (modalType === ModalType.New) {
        formData = {
          ...data,
          ...getColorSubmitValues(),
        };
      } else {
        formData = {
          ...getDirtyValues(dirtyFields, {
            ...data,
            ...getColorSubmitValues(),
          }),
          sendEmailNotification: data.sendEmailNotification ?? false,
          id: data.id,
        };
      }
      const mainLogoPromise = formData.mainLogo ? validateImage(formData.mainLogo as File, 'mainLogo') : {};
      const sidebarLogoPromise = formData.sidebarLogo ? validateImage(formData.sidebarLogo as File, 'sidebarLogo') : {};
      const faviconPromise = formData.favicon ? validateImage(formData.favicon as File, 'favicon') : {};

      const failedValidationResults = (
        await Promise.allSettled([mainLogoPromise, sidebarLogoPromise, faviconPromise])
      ).filter(result => result.status === 'rejected');

      if (failedValidationResults.length) {
        failedValidationResults.forEach(result => {
          setError(result.reason.name, { message: 'Must be a valid image.', type: 'validate' });
        });
        return;
      }
      try {
        await onSubmit(formData);
      } catch (error) {
        if (isAxiosError(error) && error.response?.data?.errors?.length) {
          handleApiValidationErrors(error.response.data.errors, setError);
        }
        throw error;
      }
      handleClose();
    },
    [modalType, getColorSubmitValues, dirtyFields, onSubmit, handleClose, setError],
  );

  const onRerunVerification = useCallback(async () => {
    setLoading(true);
    try {
      await rerunVerification(selectedWhitelabel.id);
      setSelectedWhitelabel({ ...selectedWhitelabel, domainExternalStatus: WhitelabelDomainStatus.Pending });
      fetchWhitelabels();
    } finally {
      setLoading(false);
    }
  }, [setSelectedWhitelabel, selectedWhitelabel]);

  const domainPlaceholder = useMemo(
    () => `whitelabel.${organization?.domainExternal || 'organization.domain'}`,
    [organization],
  );

  return (
    <LoadingBackdropWrapper backdropOpen={loading}>
      <Dialog maxWidth={false} open={open} onClose={onClose}>
        <DialogContent sx={{ width: '650px' }}>
          <ModalTitleCard titleIcon={<ModalHeaderIcon />} handleClose={handleClose} title={MODAL_TITLES[modalType]} />
          <FormProvider {...formMethods}>
            <form className='mt-4 space-y-5' onSubmit={handleSubmit(onFormSubmit)}>
              <div className='space-y-4'>
                {showAdminLevel ? (
                  <div>
                    <InputLabel id='type-select-label'>Type</InputLabel>
                    <Controller
                      name='type'
                      control={control}
                      render={({ field }) => (
                        <Select {...field} disabled={disabled} labelId='type-select-label' id='type-select' fullWidth>
                          {Object.values(WhitelabelType).map((option, i) => (
                            <MenuItem key={i} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </div>
                ) : null}
                <div className='flex gap-4'>
                  {whitelabelType === WhitelabelType.Whitelabel && showAdminLevel ? (
                    <Controller
                      name='organization'
                      control={control}
                      rules={{
                        required: {
                          message: 'Organization is required',
                          value: true,
                        },
                      }}
                      render={({ field, fieldState: { error } }) => (
                        <div className='flex-1'>
                          <InputLabel htmlFor='organization'>Organization</InputLabel>
                          <Autocomplete
                            {...field}
                            id='organization'
                            sx={{ marginBottom: '8px' }}
                            options={orgs}
                            fullWidth
                            disabled={disabled}
                            onChange={(_, data) => field.onChange(data)}
                            getOptionLabel={option => option?.label || ''}
                            disableClearable
                            isOptionEqualToValue={(option, selectValue) =>
                              selectValue && option.value === selectValue?.value
                            }
                            getOptionKey={option => option.value ?? option.label}
                            renderInput={params => (
                              <TextField
                                {...params}
                                error={!!error}
                                helperText={error?.message}
                                placeholder='Organization'
                                variant='outlined'
                              />
                            )}
                          />
                        </div>
                      )}
                    />
                  ) : null}
                  <Controller
                    name='name'
                    control={control}
                    rules={{
                      required: {
                        message: `${controlLabel} is required`,
                        value: true,
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <div className='flex-1'>
                        <InputLabel htmlFor='organization-name'>{controlLabel}</InputLabel>
                        <TextField
                          {...field}
                          fullWidth
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          placeholder='Acme Inc.'
                          id='organization-name'
                        />
                      </div>
                    )}
                  />
                </div>
              </div>
              <hr className='-mx-6' />
              <div>
                <p className='font-medium text-xl mb-3'>Customization</p>
                <div className='flex gap-4 mb-3'>
                  <ImageUpload title='Business Logo' name='mainLogo' required background={loginBg} />
                  <ImageUpload title='Sidebar Logo' name='sidebarLogo' background={sidebarBg} />
                </div>
                <ImageUpload title='Favicon' name='favicon' required />
                <div className='grid grid-cols-2 gap-4 items-end'>
                  <ColorInput
                    ref={sidebarBgRef}
                    className='col-span-1'
                    name='sidebarBg'
                    title='Sidebar Background Color'
                    placeholder='linear-gradient(#e66465, #9198e5)'
                  />
                  <SwitchToggle className='col-span-1' name='loginTextDark' title='Login Screen Text Dark' />
                  <ColorInput
                    ref={loginBgRef}
                    className='col-span-1'
                    name='loginBg'
                    title='Login Screen Background Color'
                    placeholder={Color.Purple}
                  />
                  <ColorInput
                    ref={sidebarTextColorRef}
                    className='col-span-1'
                    name='sidebarTextColor'
                    title='Sidebar Text Color'
                    placeholder='#FFFFFF'
                  />
                  <ColorInput
                    ref={buttonBgRef}
                    className='col-span-1'
                    name='buttonBg'
                    title='Login Screen Button Color'
                    placeholder='#1B4332'
                  />
                </div>
              </div>
              <hr className='-mx-6' />
              <div>
                <div className='mb-2 flex items-center gap-2'>
                  <InputLabel htmlFor='domain-url' sx={{ margin: 0 }}>
                    Internal Domain URL
                  </InputLabel>
                </div>
                <Controller
                  name='domain'
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: 'Domain is required',
                    },
                    ...getDomainValidationRules('domain'),
                  }}
                  disabled={domainDisabled}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      placeholder={domainPlaceholder}
                      id='domain-url'
                    />
                  )}
                />
                <DomainStatus selectedWhitelabel={selectedWhitelabel} />
              </div>
              <div>
                <div className='mb-2 flex items-center gap-2'>
                  <InputLabel htmlFor='external-domain-url' sx={{ margin: 0 }}>
                    External Domain URL
                  </InputLabel>
                </div>
                <Controller
                  name='domainExternal'
                  control={control}
                  rules={getDomainValidationRules('External Domain')}
                  disabled={externalDomainDisabled}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      placeholder='domain.whitelabel.com'
                      id='external-domain-url'
                    />
                  )}
                />
                <DomainStatus
                  isExternal
                  selectedWhitelabel={selectedWhitelabel}
                  onRerunVerification={onRerunVerification}
                />
              </div>
              <div>
                <InputLabel htmlFor='requires-campaign-approval'>
                  Does this whitelabel require campaigns to be approved before going live?
                </InputLabel>
                <Controller
                  name='requiresCampaignApproval'
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      fullWidth
                      renderValue={value => (value ? ApprovalStatus.YES : ApprovalStatus.NO)}
                      onChange={e => field.onChange(e.target.value === ApprovalStatus.YES)}
                    >
                      {Object.values(ApprovalStatus).map(value => (
                        <MenuItem key={value} value={value}>
                          {value}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </div>
              <div>
                <Controller
                  name='sendEmailNotification'
                  control={control}
                  defaultValue={false}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={!!field.value} />}
                      label='Notify the org administrator with an email in case of required dns changes.'
                    />
                  )}
                />
              </div>
              <hr className='-mx-6' />
              <div className='flex justify-end gap-2'>
                <Button color='inherit' variant='outlined' onClick={onClose}>
                  Cancel
                </Button>
                <Button color='primary' variant='contained' type='submit' disabled={!isDirty}>
                  Save
                </Button>
              </div>
            </form>
          </FormProvider>
        </DialogContent>
      </Dialog>
    </LoadingBackdropWrapper>
  );
};

export default WhitelabelModal;
